/* eslint react-hooks/exhaustive-deps: "warn" */
import { type MouseEvent, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './CaseStudyCard.module.scss';

export interface CaseStudyCardProps {
  /** Case-study headline, e.g. "Experian Enhances Benelux Company Data Coverage". */
  title: string;
  /** One-paragraph summary of the case study. */
  summary: string;
  /** Site-relative path to the case study, e.g. "/experian-...". */
  href: string;
  /** Optional company name shown next to the "Case study" kicker. */
  company?: string;
  /** Visual variant of the band background — 'dark' (default) or 'light'. */
  theme?: 'dark' | 'light';
}

/**
 * Shared case-study band used across landing-page testimonial slots. Renders a
 * real, linked case study instead of an invented pull-quote — one consistent
 * look site-wide. The soft glow tracks the pointer across the band. See
 * src/landing-pages/registry.tsx for the slug → case-study mapping.
 */
export const CaseStudyCard = ({ title, summary, href, company, theme = 'dark' }: CaseStudyCardProps) => {
  const { t } = useInt();
  const rootRef = useRef<HTMLElement>(null);
  const isLight = theme === 'light';

  const handleMouseMove = (event: MouseEvent<HTMLElement>) => {
    const el = rootRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--glow-x', `${event.clientX - rect.left}px`);
    el.style.setProperty('--glow-y', `${event.clientY - rect.top}px`);
  };

  return (
    <section
      ref={rootRef}
      className={cn(styles.root, isLight && styles.rootLight)}
      onMouseMove={handleMouseMove}
    >
      <span className={styles.texture} aria-hidden="true" />
      <span className={styles.glow} aria-hidden="true" />

      <article className={styles.card}>
        <span className={styles.mark} aria-hidden="true">
          &#8220;
        </span>
        <span className={styles.kicker}>
          {t('Case study')}
          {company ? ` · ${company}` : ''}
        </span>
        <TitleBlock
          className={styles.title}
          tag="h3"
          size="section"
          theme={isLight ? 'light' : 'dark'}
          align="center"
          accent="none"
          title={title}
        />
        <p className={styles.summary}>{summary}</p>
        <Button
          className={styles.cta}
          type={isLight ? 'dark-outline' : 'white-outline'}
          rounded="full"
          size="xl"
          href={href}
        >
          {t('Read the case study')} <span aria-hidden="true">&rarr;</span>
        </Button>
      </article>
    </section>
  );
};
